import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { collectAll as createCollectAll } from '@atlassian/experience-tracker';
import { ExperienceStore } from './experience-store';
import { ALLOWED_EXPERIENCES_FOR_MARKS, composeExperienceName, ufoMarkNameByPhase } from './utils';

/**
 * This wraps experience tracker and orchestrates UFOs behind feature flags.
 */
export var ExperienceTrackerAdapter = /*#__PURE__*/function () {
  function ExperienceTrackerAdapter(experienceTracker, config) {
    _classCallCheck(this, ExperienceTrackerAdapter);
    this.experienceTracker = experienceTracker;
    this.config = config;
    this.experiences = new ExperienceStore(config);
  }
  return _createClass(ExperienceTrackerAdapter, [{
    key: "startWithDeps",
    value: function startWithDeps(options, collectAll) {
      var experienceName = composeExperienceName(options);
      var customStartTime = this.config.ufoAttributes.customStartTime[options.name];
      this.experienceTracker.start(_objectSpread(_objectSpread({}, options), {}, {
        collect: createCollectAll(collectAll.map(composeExperienceName)),
        name: experienceName,
        startTime: customStartTime
      }));
      var experience = this.experiences.getOrCreateUFOExperienceWithDeps(options, collectAll);
      experience.start(customStartTime);
    }
  }, {
    key: "start",
    value: function start(options) {
      var experienceName = composeExperienceName(options);
      var customStartTime = this.config.ufoAttributes.customStartTime[options.name];
      this.experienceTracker.start(_objectSpread(_objectSpread({}, options), {}, {
        name: experienceName,
        startTime: customStartTime
      }));
      var experience = this.experiences.getOrCreateUFOExperience(options);
      experience.start(customStartTime);
    }
  }, {
    key: "fail",
    value: function fail(options) {
      var _this$experiences$get;
      var experienceName = composeExperienceName(options);
      this.experienceTracker.fail(_objectSpread(_objectSpread({}, options), {}, {
        name: experienceName
      }));
      (_this$experiences$get = this.experiences.get(experienceName)) === null || _this$experiences$get === void 0 || _this$experiences$get.failure({
        force: true,
        metadata: _objectSpread(_objectSpread({}, options.attributes), {}, {
          errorName: options.error.name,
          errorMessage: options.error.message
        })
      });
    }
  }, {
    key: "abort",
    value: function abort(options) {
      var experienceName = composeExperienceName({
        name: options.name,
        nameId: options.nameId
      });
      this.experienceTracker.abort(_objectSpread(_objectSpread({}, options), {}, {
        name: experienceName
      }));
      var experience = this.experiences.getOrCreateUFOExperience(options);
      experience.abort({
        // force: true is required because
        // UFO by default prevents manual aborts() on experiences with dependencies
        // (experiences that have defined the prop { until: untilAll(...) })
        force: true,
        metadata: _objectSpread(_objectSpread({}, options.attributes), {}, {
          abortReason: options.reason
        })
      });
    }
  }, {
    key: "abortAll",
    value: function abortAll(options) {
      this.experienceTracker.abort(_objectSpread({}, options));
      this.experiences.getAll().forEach(function (experience) {
        experience === null || experience === void 0 || experience.abort({
          // force: true is required because
          // UFO by default prevents manual aborts() on experiences with dependencies
          // (experiences that have defined the prop { until: untilAll(...) })
          force: true,
          metadata: _objectSpread(_objectSpread({}, options.attributes), {}, {
            abortReason: options.reason
          })
        });
      });
    }
  }, {
    key: "stopOnError",
    value: function stopOnError(options) {
      var _this$experiences$get2;
      var experienceName = composeExperienceName(options);
      this.experienceTracker.stopOnError(_objectSpread(_objectSpread({}, options), {}, {
        name: experienceName
      }));
      (_this$experiences$get2 = this.experiences.get(experienceName)) === null || _this$experiences$get2 === void 0 || _this$experiences$get2.failure({
        // force: true is required because
        // UFO by default prevents manual failure() on experiences with dependencies
        // (experiences that have defined the prop { until: untilAll(...) })
        force: true,
        metadata: _objectSpread(_objectSpread({}, options.attributes), {}, {
          errorName: options.error.name,
          errorMessage: options.error.message
        })
      });
    }
  }, {
    key: "succeed",
    value: function succeed(options) {
      var _this$experiences$get3;
      var experienceName = composeExperienceName(options);
      this.experienceTracker.succeed(_objectSpread(_objectSpread({}, options), {}, {
        name: experienceName
      }));
      (_this$experiences$get3 = this.experiences.get(experienceName)) === null || _this$experiences$get3 === void 0 || _this$experiences$get3.success({
        metadata: _objectSpread({}, options.attributes)
      });
    }
  }, {
    key: "_mark",
    value: function _mark(key, phase, timestamp) {
      var _this = this;
      var experiences = ALLOWED_EXPERIENCES_FOR_MARKS[key];
      experiences === null || experiences === void 0 || experiences.forEach(function (experience) {
        var ufoExperience = _this.experiences.get(experience);
        if (ufoExperience) {
          ufoExperience.mark(ufoMarkNameByPhase(key, phase), timestamp);
          return;
        }
      });
    }

    // UFO only
  }, {
    key: "markStart",
    value: function markStart(key, timestamp) {
      this._mark(key, 'start', timestamp);
    }

    // UFO only
  }, {
    key: "markEnd",
    value: function markEnd(key, timestamp) {
      this._mark(key, 'end', timestamp);
    }
  }, {
    key: "subscribe",
    value: function subscribe(subscriber) {
      return this.experienceTracker.subscribe(subscriber);
    }
  }], [{
    key: "from",
    value: function from(experienceTracker, config) {
      return new ExperienceTrackerAdapter(experienceTracker, config);
    }
  }]);
}();